import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSession,
  cancelSession,
  postToUniMarket,
} from "../lib/api";
import { productGroups, parseQuantityFromCart, getProductsBySubject } from "../lib/products";
import { useCart } from "../lib/cartStore";
import { ProductCard } from "../components/ProductCard";
import { CartSidebar } from "../components/CartSidebar";
import { SubjectSection } from "../components/SubjectSection";
import type { SessionResponse } from "../lib/types";

function PunchOutPageContent() {
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { addToCart, getCartItems } = useCart();

  // Fetch session on mount
  useEffect(() => {
    async function fetchSession() {
      try {
        const data = await getSession();
        setSession(data);

        // Hydrate cart from existing items if edit mode
        if (data.mode === "edit" && data.cart.length > 0) {
          const qty = parseQuantityFromCart(data.cart);
          // For now, we only have hsc_bio_slv
          const group = productGroups["hsc_bio_slv"];
          if (group) {
            addToCart(group, qty);
          }
        }
      } catch (err) {
        console.error("Session load failed", err);
        navigate("/error/session-expired");
      } finally {
        setLoading(false);
      }
    }

    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Navigate to finalise page to collect admin details before returning to UniMarket
  function handleSubmit() {
    const items = getCartItems();
    if (items.length === 0) return;
    navigate("/punchout/finalise");
  }

  // Handle cancel
  async function handleCancel() {
    setSubmitting(true);
    setError(null);

    try {
      const { targetUrl, cxmlPayload } = await cancelSession();
      postToUniMarket(targetUrl, cxmlPayload);
    } catch (err) {
      console.error("Cancel failed", err);
      setError("Failed to cancel. Please try again.");
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!session) {
    return null;
  }

  const productsBySubject = getProductsBySubject("unimarket");

  return (
    <div className="order-page">
      <header className="order-header">
        <img src="https://smartered.wpengine.com/wp-content/uploads/2023/01/SmarterEd-logo.svg" alt="SmarterEd" className="order-header-logo" />
        <div className="order-header-text">
          <h1>SmarterEd Order</h1>
          {session.contact?.name && <p>Welcome, {session.contact.name}</p>}
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="order-layout">
        <main className="catalog-section">
          {Array.from(productsBySubject.entries()).map(([subject, products]) => (
            <SubjectSection key={subject.id} subject={subject} productIds={products.map((group) => group.id)}>
              <div className="product-grid">
                {products.map((group) => (
                  <ProductCard key={group.id} group={group} />
                ))}
              </div>
            </SubjectSection>
          ))}
        </main>

        <aside className="cart-section">
          <CartSidebar
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitting={submitting}
            isPunchOut={true}
          />
        </aside>
      </div>
    </div>
  );
}

export function PunchOutPage() {
  return <PunchOutPageContent />;
}
